import {Alert, Group, Paper, SimpleGrid, Stack, Table, Text, Title} from "@mantine/core";
import {useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {Fragment, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {castString} from "./shared.ts";
import {ProductionMaterialItem, ProductionMaterialsResponse} from "./types.ts";
import classes from "./ProductionModuleMaterialsPage.module.css";

interface TypeOptionSummary {
    key: string;
    label: string;
    quantity: number;
    piles: number;
}

interface SystemOptionSummary {
    key: string;
    label: string;
    quantity: number;
    piles: number;
}

interface ContainerGroup {
    key: string;
    containerId: string;
    containerName: string;
    systemId: string;
    systemName: string;
    quantity: number;
    piles: number;
    materials: ProductionMaterialItem[];
}

function getTypeKey(material: ProductionMaterialItem): string {
    return material.materialTypeId || material.materialName.toLowerCase();
}

function getSystemKey(material: ProductionMaterialItem): string {
    return castString(material.location, "systemId") || castString(material.location, "systemName").toLowerCase();
}

function getQuantity(material: ProductionMaterialItem): number {
    const value = Number(material.quantity);
    return Number.isFinite(value) ? value : 0;
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value);
}

function summarizeOptions<T extends {quantity: number; piles: number}>(options: T[]): {quantity: number; piles: number} {
    return options.reduce((acc, option) => ({
        quantity: acc.quantity + option.quantity,
        piles: acc.piles + option.piles,
    }), {quantity: 0, piles: 0});
}

function ProductionModuleMaterialsPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const moduleInfo = useProductionModule();
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedSystem, setSelectedSystem] = useState<string | null>(null);

    const query = useQuery<ProductionMaterialsResponse>({
        queryKey: ["prod-module", moduleId, "materials"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/materials`)).data as ProductionMaterialsResponse,
        enabled: !!moduleId,
    });

    const materials = query.data?.materials ?? [];
    const typeOptions = useMemo<TypeOptionSummary[]>(() => {
        const filtered = selectedSystem
            ? materials.filter((material) => getSystemKey(material) === selectedSystem)
            : materials;

        const grouped = new Map<string, TypeOptionSummary>();
        filtered.forEach((material) => {
            const key = getTypeKey(material);
            const current = grouped.get(key) ?? {
                key,
                label: material.materialName || material.materialTypeId,
                quantity: 0,
                piles: 0,
            };
            current.quantity += getQuantity(material);
            current.piles += 1;
            grouped.set(key, current);
        });

        return Array.from(grouped.values())
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [materials, selectedSystem]);

    const systemOptions = useMemo<SystemOptionSummary[]>(() => {
        const filtered = selectedType
            ? materials.filter((material) => getTypeKey(material) === selectedType)
            : materials;

        const grouped = new Map<string, SystemOptionSummary>();
        filtered.forEach((material) => {
            const key = getSystemKey(material);
            const systemName = castString(material.location, "systemName");
            const systemId = castString(material.location, "systemId");
            const label = systemName || systemId;
            const current = grouped.get(key) ?? {
                key,
                label,
                quantity: 0,
                piles: 0,
            };
            current.quantity += getQuantity(material);
            current.piles += 1;
            grouped.set(key, current);
        });

        return Array.from(grouped.values())
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [materials, selectedType]);

    useEffect(() => {
        if (selectedType && !typeOptions.some((option) => option.key === selectedType)) {
            setSelectedType(null);
        }
    }, [selectedType, typeOptions]);

    useEffect(() => {
        if (selectedSystem && !systemOptions.some((option) => option.key === selectedSystem)) {
            setSelectedSystem(null);
        }
    }, [selectedSystem, systemOptions]);

    const filteredMaterials = useMemo(() => {
        return materials.filter((material) => {
            if (selectedType && getTypeKey(material) !== selectedType) {
                return false;
            }
            if (selectedSystem && getSystemKey(material) !== selectedSystem) {
                return false;
            }
            return true;
        });
    }, [materials, selectedSystem, selectedType]);

    const groupedByContainer = useMemo<ContainerGroup[]>(() => {
        const grouped = new Map<string, ContainerGroup>();

        filteredMaterials.forEach((material) => {
            const containerId = castString(material.location, "containerId");
            const containerName = castString(material.location, "containerName") || containerId;
            const systemId = castString(material.location, "systemId");
            const systemName = castString(material.location, "systemName") || systemId;
            const containerKey = containerId || containerName.toLowerCase();
            const key = `${getSystemKey(material)}:${containerKey}`;

            const current = grouped.get(key) ?? {
                key,
                containerId,
                containerName,
                systemId,
                systemName,
                quantity: 0,
                piles: 0,
                materials: [],
            };
            current.quantity += getQuantity(material);
            current.piles += 1;
            current.materials.push(material);
            grouped.set(key, current);
        });

        return Array.from(grouped.values())
            .sort((a, b) => {
                const systemCompare = a.systemName.localeCompare(b.systemName);
                if (systemCompare !== 0) {
                    return systemCompare;
                }
                return a.containerName.localeCompare(b.containerName);
            })
            .map((group) => ({
                ...group,
                materials: [...group.materials].sort((a, b) => {
                    const nameCompare = String(a.materialName || "").localeCompare(String(b.materialName || ""));
                    if (nameCompare !== 0) {
                        return nameCompare;
                    }
                    return getQuantity(b) - getQuantity(a);
                }),
            }));
    }, [filteredMaterials]);

    const typeSummary = useMemo(() => summarizeOptions(typeOptions), [typeOptions]);
    const systemSummary = useMemo(() => summarizeOptions(systemOptions), [systemOptions]);

    const hasFilter = !!selectedType || !!selectedSystem;

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }
    if (query.isPending) {
        return <LoaderScreen message="Loading materials"/>;
    }
    if (query.isError || !query.data) {
        return <Page><Alert color="red" title="Materials unavailable">Could not load material data.</Alert></Page>;
    }

    return (
        <Page>
            <title>{moduleInfo?.label || 'Production'} - Materials</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Materials</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                <Paper withBorder p="md">
                    <Stack gap="xs">
                        <Text fw={700}>Filters</Text>
                        <Text size="sm" c="dimmed">
                            Material piles: {formatNumber(materials.length)} | Last sync: {query.data.materialsLastSyncAt ? DateTime.fromSeconds(query.data.materialsLastSyncAt).toFormat("yyyy-LL-dd HH:mm") : "never"} | Source rows: {formatNumber(query.data.materialsSourceRows)}
                        </Text>
                        <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                            <Paper withBorder p={0}>
                                <Table withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>RM Type</Table.Th>
                                            <Table.Th ta="right">Qty</Table.Th>
                                            <Table.Th ta="right">Piles</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        <Table.Tr
                                            className={classes.filterRow}
                                            data-active={selectedType === null}
                                            onClick={() => setSelectedType(null)}
                                        >
                                            <Table.Td>All Types</Table.Td>
                                            <Table.Td ta="right">{formatNumber(typeSummary.quantity)}</Table.Td>
                                            <Table.Td ta="right">{formatNumber(typeSummary.piles)}</Table.Td>
                                        </Table.Tr>
                                        {typeOptions.map((option) => (
                                            <Table.Tr
                                                key={option.key}
                                                className={classes.filterRow}
                                                data-active={selectedType === option.key}
                                                onClick={() => setSelectedType(option.key)}
                                            >
                                                <Table.Td>{option.label}</Table.Td>
                                                <Table.Td ta="right">{formatNumber(option.quantity)}</Table.Td>
                                                <Table.Td ta="right">{formatNumber(option.piles)}</Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Paper>

                            <Paper withBorder p={0}>
                                <Table withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>System</Table.Th>
                                            <Table.Th ta="right">Qty</Table.Th>
                                            <Table.Th ta="right">Piles</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        <Table.Tr
                                            className={classes.filterRow}
                                            data-active={selectedSystem === null}
                                            onClick={() => setSelectedSystem(null)}
                                        >
                                            <Table.Td>All Systems</Table.Td>
                                            <Table.Td ta="right">{formatNumber(systemSummary.quantity)}</Table.Td>
                                            <Table.Td ta="right">{formatNumber(systemSummary.piles)}</Table.Td>
                                        </Table.Tr>
                                        {systemOptions.map((option) => (
                                            <Table.Tr
                                                key={option.key}
                                                className={classes.filterRow}
                                                data-active={selectedSystem === option.key}
                                                onClick={() => setSelectedSystem(option.key)}
                                            >
                                                <Table.Td>{option.label}</Table.Td>
                                                <Table.Td ta="right">{formatNumber(option.quantity)}</Table.Td>
                                                <Table.Td ta="right">{formatNumber(option.piles)}</Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Paper>
                        </SimpleGrid>
                    </Stack>
                </Paper>

                {hasFilter ? (
                    <Paper withBorder p="md">
                        <Stack gap="xs">
                            <Text fw={700}>
                                Matching Piles by Container ({formatNumber(filteredMaterials.length)} piles in {formatNumber(groupedByContainer.length)} containers)
                            </Text>
                            <Table withTableBorder>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Pile Name</Table.Th>
                                        <Table.Th>Material</Table.Th>
                                        <Table.Th>Quantity</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {groupedByContainer.map((group) => (
                                        <Fragment key={group.key}>
                                            <Table.Tr>
                                                <Table.Td colSpan={4}>
                                                    <Group justify="space-between" wrap="nowrap">
                                                        <Text fw={600}>
                                                            {group.containerName}
                                                        </Text>
                                                        {group.materials.length > 1 ? (<Text size="sm" c="dimmed">
                                                            {group.systemName} | {formatNumber(group.quantity)} qty | {formatNumber(group.piles)} piles
                                                        </Text>) : null}
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                            {group.materials.map((material) => (
                                                <Table.Tr key={material.materialId}>
                                                    <Table.Td>{material.pileName}</Table.Td>
                                                    <Table.Td>{material.materialName}</Table.Td>
                                                    <Table.Td>{formatNumber(getQuantity(material))}</Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Fragment>
                                    ))}
                                    {groupedByContainer.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={4}>
                                                <Text c="dimmed">No piles match the selected filters.</Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : null}
                                </Table.Tbody>
                            </Table>
                        </Stack>
                    </Paper>
                ) : (
                    <Alert color="blue" title="Filter Required">
                        Apply at least one filter to display grouped material piles.
                    </Alert>
                )}
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleMaterialsPage);
